import { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react'
import Tree from 'react-d3-tree'
import { getLinkSpeed } from '../utils'
import { SCALE_MIN, SCALE_MAX, SCALE_STEP } from '../constants'
import { TreeCanvasWrapper } from '../Styled'
import TopologyNode from './Node'
import TreeControls from './TreeControls'

const LINK_STYLES = `
  @keyframes topo-pulse {
    0% { r: 18; opacity: 0.5; }
    100% { r: 26; opacity: 0; }
  }
  .rd3t-tree-container svg .topo-link-hidden { stroke: transparent; }
  .rd3t-tree-container svg .topo-link-orphan { stroke: #1f2937; stroke-width: 2px; }
  .rd3t-tree-container svg .topo-link-speed { stroke-width: 2px; }
  .rd3t-tree-container svg .topo-link-10gb { stroke: #4F88EC; }
  .rd3t-tree-container svg .topo-link-1gb { stroke: #A7D59F; }
  .rd3t-tree-container svg .topo-link-100mb { stroke: #DBC76D; }
  .rd3t-tree-container svg .topo-link-10mb { stroke: #B76E71; }
  .rd3t-tree-container svg .topo-link-unknow { stroke: #A8AAAA; }
  .rd3t-tree-container svg .topo-link-dashed { stroke-dasharray: 6; }
`

const getPathClass = (linkData) => {
  const target = linkData.target.data
  if (target.isVirtualRoot) return 'topo-link-hidden'
  if (target.isOrphan) return 'topo-link-orphan'
  const speed = `topo-link-speed topo-link-${getLinkSpeed(target.linkSpeed)}`
  const isDashed = target.positionless || target.isSameBuilding === false
  const dashed = isDashed ? ' topo-link-dashed' : ''
  return speed + dashed
}

const getTreeDepth = node => node
  ? 1 + Math.max(0, ...(node.children || []).map(getTreeDepth))
  : 0

const getPreviewTranslate = (width, height, treeData, nodeWidth) => ({
  x: width / 2 - (Math.max(getTreeDepth(treeData) - 1, 0) * nodeWidth) / 2,
  y: height / 2
})

const TopologyTree = forwardRef(({ treeData, selectedMac, onSelectNode, onHoverNode, onUnhoverNode, onRevealChildren, onHideRevealedChildren, revealedParentMacs, preview = false }, ref) => {
  const containerRef = useRef(null)
  const [translate, setTranslate] = useState({ x: 300, y: 300 })
  const [scale, setScale] = useState(1)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const [separation, setSeparation] = useState({ siblings: 4, nonSiblings: 4.5 })
  const [nodeWidth, setNodeWidth] = useState(preview ? 140 : 280)

  const initializedRef = useRef(false)
  useEffect(() => {
    if (!containerRef.current) return
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      if (width > 0 && height > 0) {
        setDimensions({ width, height })
        if (!initializedRef.current) {
          setTranslate(preview
            ? getPreviewTranslate(width, height, treeData, nodeWidth)
            : { x: width / 4, y: height / 2 })
          initializedRef.current = true
        }
      }
    })
    ro.observe(containerRef.current)
    return () => ro.disconnect()
  }, [nodeWidth, preview, treeData])

  const focusNode = useCallback((mac) => {
    if (!containerRef.current) return false
    const el = containerRef.current.querySelector(`[data-mac="${mac}"]`)
    if (!el) return false
    const rect = el.getBoundingClientRect()
    const cRect = containerRef.current.getBoundingClientRect()
    setTranslate(prev => ({
      x: prev.x + (cRect.width / 2 - (rect.left - cRect.left + rect.width / 2)),
      y: prev.y + (cRect.height / 2 - (rect.top - cRect.top + rect.height / 2)),
    }))
    return true
  }, [])

  useImperativeHandle(ref, () => ({ focusNode }), [focusNode])

  useEffect(() => {
    if (preview || !selectedMac || dimensions.width === 0) return
    window.requestAnimationFrame(() => focusNode(selectedMac))
  }, [selectedMac, dimensions.width, focusNode, preview])

  useEffect(() => {
    if (!preview || dimensions.width === 0 || dimensions.height === 0) return
    setTranslate(getPreviewTranslate(dimensions.width, dimensions.height, treeData, nodeWidth))
  }, [dimensions.height, dimensions.width, nodeWidth, preview, treeData])

  const handleZoomIn = useCallback(() => setScale(s => Math.min(+(s + SCALE_STEP).toFixed(1), SCALE_MAX)), [])
  const handleZoomOut = useCallback(() => setScale(s => Math.max(+(s - SCALE_STEP).toFixed(1), SCALE_MIN)), [])
  const handleCenter = useCallback(() => {
    if (!containerRef.current) return
    const { width, height } = containerRef.current.getBoundingClientRect()
    setTranslate(preview
      ? getPreviewTranslate(width, height, treeData, nodeWidth)
      : { x: width / 4, y: height / 2 })
    setScale(1)
  }, [nodeWidth, preview, treeData])
  const handleSpreadMore = useCallback((axis) => {
    if (axis === 'v') {
      setSeparation(p => ({
        siblings: Math.min(p.siblings + 0.5, 10),
        nonSiblings: Math.min(p.nonSiblings + 0.5, 10),
      }))
    } else {
      setNodeWidth(p => Math.min(p + 50, 600))
    }
  }, [])
  const handleSpreadLess = useCallback((axis) => {
    if (axis === 'v') {
      setSeparation(p => ({
        siblings: Math.max(p.siblings - 0.5, 1),
        nonSiblings: Math.max(p.nonSiblings - 0.5, 1),
      }))
    } else {
      setNodeWidth(p => Math.max(p - 50, 50))
    }
  }, [])

  const renderNode = useCallback(({ nodeDatum, toggleNode, hierarchyPointNode }) => (
    <TopologyNode
      nodeDatum={nodeDatum}
      selectedMac={selectedMac}
      onSelectNode={onSelectNode}
      onHoverNode={onHoverNode}
      onUnhoverNode={onUnhoverNode}
      toggleNode={toggleNode}
      nodeWidth={nodeWidth}
      hierarchyPointNode={hierarchyPointNode}
      onRevealChildren={onRevealChildren}
      onHideRevealedChildren={onHideRevealedChildren}
      isChildrenForceRevealed={revealedParentMacs?.has(nodeDatum.mac_address)}
      preview={preview}
    />
  ), [selectedMac, onSelectNode, onHoverNode, onUnhoverNode, nodeWidth, onRevealChildren, onHideRevealedChildren, preview, revealedParentMacs])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const prevent = (e) => { if (e.ctrlKey || e.metaKey) e.preventDefault() }
    el.addEventListener('wheel', prevent, { passive: false })
    return () => el.removeEventListener('wheel', prevent)
  }, [])

  if (!treeData) return null

  return (
    <TreeCanvasWrapper ref={containerRef}>
      <style>{LINK_STYLES}</style>

      {dimensions.width > 0 && (
        <Tree
          data={treeData}
          orientation="horizontal"
          nodeSize={{ x: nodeWidth, y: 18 }}
          dimensions={dimensions}
          renderCustomNodeElement={renderNode}
          separation={separation}
          translate={translate}
          zoom={scale}
          scaleExtent={{ min: SCALE_MIN, max: SCALE_MAX }}
          zoomable
          draggable
          enableLegacyTransitions={false}
          transitionDuration={0}
          pathClassFunc={getPathClass}
          onUpdate={({ translate: t, zoom: z }) => {
            setTranslate(t)
            setScale(z)
          }}
        />
      )}

      <TreeControls
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onCenter={handleCenter}
        onSpreadMore={handleSpreadMore}
        onSpreadLess={handleSpreadLess}
        compact={preview}
      />
    </TreeCanvasWrapper>
  )
})

export default TopologyTree
